/**
 * Chat copy and confirm chips for a schedule draft.
 * The draft comes from POST /api/chat/schedule. Nothing is written here.
 */

export const SCHEDULE_CHAT_TEXT = {
  whichDay: "Welcher Tag?",
  whichItem: "Welchen Termin?",
  missingTitle: "Wie soll der Termin heißen?",
  notFound: "Ich finde keinen passenden Termin.",
  noHousehold: "Für uns geht erst, wenn der Partner dabei ist.",
  agendaMiss: "Dazu habe ich nichts Offenes.",
  cancelled: "Alles klar — bleibt so.",
  saveFailed: "Das Speichern hat gerade nicht geklappt. Versuch es gleich nochmal.",
  save: "Merken",
  change: "Ändern",
  done: "Erledigt",
  cancel: "Abbrechen",
  decline: "Nein",
  leave: "So lassen",
  skip: "Nicht",
};

const WEEKDAY_LABEL = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];
const MONTH_LABEL = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
];

function normalizeScheduleGate(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/ß/g, "ss")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/(\d)\s*[-–—]\s*(\d)/g, "$1-$2")
    .replace(/(\d{1,2}):(\d{2})/g, "$1§$2")
    .replace(/:/g, " ")
    .replace(/§/g, ":")
    .replace(/[^a-z0-9.:\s-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

const TOPIC_STOP = new Set([
  "heute", "morgen", "uebermorgen", "ubermorgen", "today", "tomorrow",
  "montag", "dienstag", "mittwoch", "donnerstag", "freitag", "samstag", "sonntag",
  "monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday",
  "januar", "februar", "marz", "maerz", "april", "mai", "juni", "juli", "august",
  "september", "oktober", "november", "dezember",
  "wann", "when", "hab", "habe", "ich", "do", "have", "is", "there", "gibt", "es", "hast", "du",
  "der", "die", "das", "den", "dem", "ein", "eine", "einen", "the", "on", "at", "um", "uhr", "to", "for",
  "bitte", "please", "mein", "meine", "meinen", "my", "am", "pm", "bis", "und", "oder", "nicht",
  "kein", "keine", "termin", "aufgabe", "uhrzeit", "time", "zeit", "lust", "bock", "ahnung", "idee",
  "ist", "sind", "war", "waren", "wird",
  "fuer", "fur", "uns", "mich", "mir", "dir", "im", "in", "naechste", "nachste", "nachsten",
  "naechsten", "diesen", "diese", "dieser", "next", "oclock",
]);

function scheduleTopic(t) {
  return String(t || "")
    .replace(/\b\d{1,2}\s*(?:-|bis)\s*\d{1,2}\b/g, " ")
    .replace(/\b\d{1,2}(?::\d{2})?\s*(?:am|pm|uhr|oclock)\b/g, " ")
    .replace(/\b(?:um|auf|at)\s+\d{1,2}(?::\d{2})?\b/g, " ")
    .replace(/\b\d{1,2}\.\d{1,2}(?:\.\d{2,4})?\b/g, " ")
    .replace(/\b\d{1,2}(?::\d{2})?\b/g, " ")
    .split(" ")
    .filter((word) => word.length >= 3 && !TOPIC_STOP.has(word))
    .join(" ");
}

function hasScheduleClock(t) {
  return (
    /\b\d{1,2}\s*(?:-|bis)\s*\d{1,2}\b/.test(t) ||
    /\b\d{1,2}(?::\d{2})?\s*(?:am|pm)\b/.test(t) ||
    /\b(?:um|auf)\s+\d{1,2}\b(?![:.])/.test(t) ||
    /\bat\s+\d{1,2}(?::\d{2})?(?:\s+oclock)?\b/.test(t) ||
    /\b\d{1,2}(?::\d{2})?\s*uhr\b/.test(t) ||
    /\b\d{1,2}:\d{2}\b/.test(t)
  );
}

function hasScheduleAnchor(t) {
  if (hasScheduleClock(t)) return true;
  if (
    /\b(?:heute|morgen|uebermorgen|ubermorgen|today|tomorrow|montag|dienstag|mittwoch|donnerstag|freitag|samstag|sonntag|monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b/.test(
      t,
    )
  ) {
    return true;
  }
  if (/\b\d{1,2}\.\d{1,2}(?:\.\d{2,4})?\b/.test(t)) return true;
  return /\b\d{1,2}\.?\s+(?:januar|februar|marz|maerz|april|mai|juni|juli|august|september|oktober|november|dezember)\b/.test(
    t,
  );
}

/** Schulferien, Sommerferien, and the same compounds. A word that only starts with ferien stays a Termin. */
function isHolidayQuestionTopic(t) {
  return /\b(?:[a-z]*ferien|feiertage?)\b/.test(t);
}

function isScheduleQuestion(t) {
  if (!t || isHolidayQuestionTopic(t)) return false;
  if (scheduleTopic(t).length < 3) return false;
  if (/^(?:wann|when)\b/.test(t)) return true;
  return /\b(?:hab ich|habe ich|do i have|have i|is there)\b/.test(t);
}

/**
 * Same gate as server/lib/scheduleChat.js — keep the verb list in step.
 * @param {string} text
 * @param {{ focus?: { title?: string }|null }} [opts]
 */
export function looksLikeScheduleWrite(text, { focus } = {}) {
  const t = normalizeScheduleGate(text);
  if (!t || t.length > 280) return false;
  if (
    /\b(mail|email|e mail|flyer|foto|zettel|elternbrief)\b/.test(t) &&
    /\b(loschen|loeschen|vergiss|ignorier|nicht relevant|nicht fur uns)\b/.test(t)
  ) {
    return false;
  }
  if (/\b(?:ferien|feiertag|feiertage)\b/.test(t)) return false;
  if (isScheduleQuestion(t)) return true;
  if (/^(?:was|wo|wer|wie|welche|welcher|welchen|gibt|hast|zeig)\b/.test(t)) return false;
  if (/^(?:wann|when)\b/.test(t)) return false;
  const clock = hasScheduleClock(t);
  if (focus?.title && clock) return true;
  if (/\b(erledigt|abgehakt)\b/.test(t)) return true;
  if (/\b(verschieb\w*|verleg\w*|schieb\w*)\b/.test(t)) return true;
  if (/\b(aendere|aender|aendern|aendert|ander)\b/.test(t)) return true;
  if (/\b(andere|andern|andert)\b/.test(t) && /\b(auf|am|zum|in|zu|den|die|das|dem)\b/.test(t)) {
    return true;
  }
  if (/\b(?:setze|setzen|setzt|setz)\b/.test(t) && /\b(auf|am|zum|in|zu)\b/.test(t)) return true;
  if (/\b(?:lege|legen|legt|leg)\b/.test(t) && /\b(auf|am|zum)\b/.test(t)) return true;
  if (/\b(?:benenne|nenne|nenn)\b/.test(t)) return true;
  if (/\b(?:change(?:s)?|move)\b/.test(t) && clock) return true;
  if (/\bset\b/.test(t) && clock) return true;
  if (/\berinnerung\s+(?:an|aus)\b/.test(t)) return true;
  if (/\b(erinnere|erinner mich|erinnern|merk\w*|eintrag\w*|\btrag\b)\b/.test(t)) return true;
  if (/\b(neue aufgabe|neuer termin|neuen termin)\b/.test(t)) return true;
  if (/\b(ist|sind|war|waren|wird|wichtig|auch|nicht)\b/.test(t)) return false;
  if (t.split(" ").length <= 6 && /\bfur uns$/.test(t)) return true;
  if (t.split(" ").length <= 6 && /\bnur\s+(?:fur\s+)?(?:mich|ich)$/.test(t)) return true;
  if (t.split(" ").length > 10) return false;
  if (
    /\b(?:bin|bist|seid|war|waren|wird|werde|mude|krank|fuhl|fuehl|gehe|geht|kann|koenn|konn|will|mocht|moechte|muss|soll|glaube|denke|weiss|weis|hunger|schlaf|tired|feel|going|gonna|want|need|think|wichtig|auch|ist|sind|nicht)\b/.test(
      t,
    )
  ) {
    return false;
  }
  if (!hasScheduleAnchor(t)) return false;
  return scheduleTopic(t).length >= 3;
}

/**
 * Remember the Termin a confirm or a saved row just named.
 * @param {object|null|undefined} turn
 */
export function scheduleFocusFromTurn(turn) {
  if (turn?.action === "reply" && turn.reason === "agenda_hit" && turn.item?.title) {
    return { itemId: turn.item.id || null, title: turn.item.title, draft: null };
  }
  if (!turn || turn.action === "none" || turn.action === "reply") return null;
  const title = String(turn.draft?.newTitle || turn.draft?.title || turn.item?.title || "").trim();
  if (!title) return null;
  return {
    itemId: turn.item?.id || null,
    title,
    draft: turn.draft || null,
  };
}

function weekdayIndex(ymd) {
  const [y, m, d] = String(ymd || "").split("-").map((part) => parseInt(part, 10));
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

function addDaysYmd(ymd, days) {
  const [y, m, d] = String(ymd || "").split("-").map((part) => parseInt(part, 10));
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}

/**
 * @param {string} ymd
 * @param {string} [todayYmd]
 */
export function formatScheduleDate(ymd, todayYmd) {
  const [year, month, day] = String(ymd || "").split("-").map((part) => parseInt(part, 10));
  if (!year || !month || !day) return "";
  const label = `${WEEKDAY_LABEL[weekdayIndex(ymd)]}, ${day}. ${MONTH_LABEL[month - 1]}`;
  const todayYear = todayYmd ? parseInt(String(todayYmd).slice(0, 4), 10) : year;
  return year !== todayYear ? `${label} ${year}` : label;
}

function formatWhen(draft, todayYmd) {
  const bits = [];
  if (draft?.date) bits.push(formatScheduleDate(draft.date, todayYmd));
  if (draft?.time) bits.push(draft.time);
  return bits.join(", ");
}

function dateChoiceLabel(date, todayYmd) {
  const pretty = formatScheduleDate(date, todayYmd);
  if (date === todayYmd) return `Heute, ${pretty}`;
  if (date === addDaysYmd(todayYmd, 1)) return `Morgen, ${pretty}`;
  return pretty;
}

/**
 * @param {object|null|undefined} turn
 */
export function scheduleConfirmText(turn) {
  const draft = turn?.draft || {};
  const title = draft.title || "Termin";
  const when = formatWhen(draft, turn?.todayYmd);
  if (turn?.mode === "create") {
    const bits = [title];
    if (when) bits[0] = `${title}, ${when}`;
    const sentence = [bits[0]];
    if (draft.actionType === "task") sentence.push("Aufgabe");
    if (draft.remind === false) sentence.push("Erinnerung aus");
    else if (draft.remind === true || draft.actionType === "task") sentence.push("Erinnerung an");
    if (draft.visibility === "household") sentence.push("Für uns");
    if (draft.visibility === "mine") sentence.push("Nur ich");
    return `${sentence.join(". ")}. Merken?`.replace("..", ".");
  }
  if (draft.done) return `${title} als erledigt markieren?`;
  if (draft.newTitle && !when) return `${title} heißt dann ${draft.newTitle}?`;
  if (draft.visibility && !when && draft.remind == null && !draft.place && !draft.newTitle) {
    return draft.visibility === "household" ? `${title} für uns?` : `${title} nur für mich?`;
  }
  const extras = [];
  if (draft.remind === true) extras.push("Erinnerung an");
  if (draft.remind === false) extras.push("Erinnerung aus");
  if (draft.visibility === "household") extras.push("Für uns");
  if (draft.visibility === "mine") extras.push("Nur ich");
  if (draft.place) extras.push(draft.place);
  if (draft.newTitle) extras.push(`heißt dann ${draft.newTitle}`);
  if (when && extras.length) return `${title} auf ${when}. ${extras.join(". ")}?`;
  if (when) return `${title} auf ${when}?`;
  if (extras.length) return `${title}: ${extras.join(". ")}?`;
  return `${title} ändern?`;
}

/**
 * @param {object|null|undefined} turn
 */
export function scheduleSavedText(turn) {
  const draft = turn?.draft || {};
  const title = draft.newTitle || draft.title || "Termin";
  const when = formatWhen({ ...draft, title }, turn?.todayYmd);
  if (draft.done) return `${title} ist erledigt.`;
  if (turn?.mode === "create") {
    return when ? `Gemerkt. ${title}, ${when}.` : `Gemerkt. ${title}.`;
  }
  return when ? `Geändert. ${title}, ${when}.` : `Geändert. ${title}.`;
}

function slotPhrase(date, time, todayYmd) {
  let day = "";
  if (date && todayYmd) {
    if (date === todayYmd) day = "heute";
    else if (date === addDaysYmd(todayYmd, 1)) day = "morgen";
    else if (date === addDaysYmd(todayYmd, 2)) day = "übermorgen";
    else day = `am ${formatScheduleDate(date, todayYmd)}`;
  }
  if (day && time) return `${day} um ${time}`;
  if (day) return day;
  return time ? `um ${time}` : "";
}

function offerText(turn) {
  const title = turn?.draft?.title || "Termin";
  const slot = slotPhrase(turn?.draft?.date, turn?.draft?.time, turn?.todayYmd);
  return slot ? `${title} ${slot} merken?` : `${title} merken?`;
}

function sameNameText(turn) {
  const title = turn?.item?.title || turn?.draft?.title || "Termin";
  const slot = slotPhrase(turn?.item?.date, turn?.item?.time, turn?.todayYmd);
  return slot ? `${title} steht schon ${slot}.` : `${title} steht schon.`;
}

function timeClashText(turn) {
  const time = turn?.draft?.time || turn?.clash?.time;
  const title = turn?.clash?.title || "ein Termin";
  return time ? `Um ${time} steht schon ${title}.` : `Da steht schon ${title}.`;
}

function agendaHitText(turn) {
  const title = turn?.item?.title || "Termin";
  const slot = slotPhrase(turn?.item?.date, turn?.item?.time, turn?.todayYmd);
  const line = slot ? `${title} steht ${slot}.` : `${title} steht ohne Tag.`;
  if (turn?.askedDate && turn.item?.date === turn.askedDate) return `Ja. ${line}`;
  return line;
}

function agendaManyText(turn) {
  const bits = (turn?.items || []).slice(0, 3).map((item) => {
    const slot = slotPhrase(item?.date, item?.time, turn?.todayYmd);
    return slot ? `${item.title} ${slot}` : item.title;
  });
  return bits.length ? `Dazu gibt es mehrere: ${bits.join(", ")}.` : "Dazu gibt es mehrere.";
}

function replyText(turn) {
  if (turn?.reason === "missing_title") return SCHEDULE_CHAT_TEXT.missingTitle;
  if (turn?.reason === "no_household") return SCHEDULE_CHAT_TEXT.noHousehold;
  if (turn?.reason === "not_found") return SCHEDULE_CHAT_TEXT.notFound;
  if (turn?.reason === "agenda_miss") return SCHEDULE_CHAT_TEXT.agendaMiss;
  if (turn?.reason === "agenda_hit") return agendaHitText(turn);
  if (turn?.reason === "agenda_many") return agendaManyText(turn);
  if (turn?.reason === "already") return sameNameText(turn);
  return null;
}

function confirmMessageText(turn) {
  if (turn?.conflict === "offer") return offerText(turn);
  if (turn?.conflict === "same_name") return sameNameText(turn);
  if (turn?.conflict === "time") return timeClashText(turn);
  return scheduleConfirmText(turn);
}

function payloadFromTurn(turn, extra = {}) {
  return {
    mode: turn.mode,
    todayYmd: turn.todayYmd,
    itemId: turn.item?.id || null,
    itemTitle: turn.item?.title || turn.draft?.title || null,
    itemDate: turn.item?.date || null,
    draft: turn.draft,
    dateChoices: turn.dateChoices || [],
    conflict: turn.conflict || null,
    clash: turn.clash || null,
    ...extra,
  };
}

/**
 * @param {object|null|undefined} turn
 * @param {() => string} makeId
 */
export function messageFromScheduleTurn(turn, makeId) {
  if (!turn || turn.action === "none") return null;
  if (turn.action === "reply") {
    const text = replyText(turn);
    return text ? { text } : null;
  }
  if (turn.action === "clarify" && turn.clarify === "date") {
    const choices = Array.isArray(turn.dateChoices) ? turn.dateChoices : [];
    return {
      text: SCHEDULE_CHAT_TEXT.whichDay,
      options: choices.map((date) => ({
        id: makeId(),
        label: dateChoiceLabel(date, turn.todayYmd),
        kind: "schedule-pick-date",
        value: payloadFromTurn(turn, { date }),
      })),
    };
  }
  if (turn.action === "clarify" && turn.clarify === "item") {
    const choices = Array.isArray(turn.itemChoices) ? turn.itemChoices : [];
    return {
      text: SCHEDULE_CHAT_TEXT.whichItem,
      options: choices.map((item) => ({
        id: makeId(),
        label: item.date
          ? `${item.title} · ${formatScheduleDate(item.date, turn.todayYmd)}`
          : item.title,
        kind: "schedule-pick-item",
        value: payloadFromTurn(turn, {
          mode: "update",
          itemId: item.id,
          itemTitle: item.title,
          itemDate: item.date || null,
        }),
      })),
    };
  }
  if (turn.action === "confirm") {
    const label = turn.conflict === "same_name"
      ? SCHEDULE_CHAT_TEXT.change
      : turn.mode === "create"
        ? SCHEDULE_CHAT_TEXT.save
        : turn.draft?.done
          ? SCHEDULE_CHAT_TEXT.done
          : SCHEDULE_CHAT_TEXT.change;
    const decline = turn.conflict === "offer"
      ? SCHEDULE_CHAT_TEXT.decline
      : turn.conflict === "same_name"
        ? SCHEDULE_CHAT_TEXT.leave
        : turn.conflict === "time"
          ? SCHEDULE_CHAT_TEXT.skip
          : SCHEDULE_CHAT_TEXT.cancel;
    return {
      text: confirmMessageText(turn),
      options: [
        {
          id: makeId(),
          label,
          kind: "schedule-confirm",
          value: payloadFromTurn(turn),
        },
        {
          id: makeId(),
          label: decline,
          kind: "schedule-cancel",
        },
      ],
    };
  }
  return null;
}

/**
 * Follow-up chip → the next confirm or date question.
 * @param {{ kind?: string, value?: object }} option
 */
export function turnAfterScheduleChoice(option) {
  const value = option?.value || {};
  if (option?.kind === "schedule-pick-date") {
    return {
      action: "confirm",
      mode: value.mode || "create",
      conflict: value.conflict || null,
      clash: value.clash || null,
      todayYmd: value.todayYmd,
      draft: { ...(value.draft || {}), date: value.date },
      item: value.itemId
        ? { id: value.itemId, title: value.itemTitle, date: value.itemDate || null }
        : null,
    };
  }
  if (option?.kind === "schedule-pick-item") {
    const draft = { ...(value.draft || {}), title: value.itemTitle || value.draft?.title };
    const item = { id: value.itemId, title: value.itemTitle, date: value.itemDate || null };
    const hasChange = Boolean(
      draft.date || draft.time || draft.place || draft.remind != null || draft.visibility || draft.done || draft.newTitle,
    );
    if (!hasChange) {
      return {
        action: "clarify",
        clarify: "date",
        mode: "update",
        todayYmd: value.todayYmd,
        draft,
        item,
        dateChoices: value.dateChoices || [],
      };
    }
    return {
      action: "confirm",
      mode: "update",
      todayYmd: value.todayYmd,
      draft,
      item,
    };
  }
  return null;
}

/**
 * Body for createScheduleItem or patchScheduleItem.
 * @param {{ mode?: string, draft?: object }} turn
 */
export function scheduleWriteBody(turn) {
  const draft = turn?.draft || {};
  if (turn?.mode === "create") {
    const actionType = draft.actionType === "task" ? "task" : "event";
    const body = {
      title: draft.newTitle || draft.title,
      date: draft.date,
      all_day: !draft.time,
      action_type: actionType,
    };
    if (draft.time) body.time = draft.time;
    if (draft.place) body.place = draft.place;
    if (draft.remind != null) body.remind = draft.remind;
    else if (actionType === "task") body.remind = true;
    if (draft.visibility) body.visibility = draft.visibility;
    return body;
  }
  const patch = {};
  if (draft.newTitle) patch.title = draft.newTitle;
  if (draft.date) patch.date = draft.date;
  if (draft.time) {
    patch.time = draft.time;
    patch.all_day = false;
  }
  if (draft.place) patch.place = draft.place;
  if (draft.remind != null) patch.remind = draft.remind;
  if (draft.visibility) patch.visibility = draft.visibility;
  if (draft.done) patch.done = true;
  return patch;
}
